/**
 * `PromptModal` — the **generic prompt body**. When the authority returns a `PromptRequest`
 * (a `choose` with no recorded answer), the acting client shows this: the prompt text + one
 * button per option. Picking an option calls `onAnswer(optionId)`, which the controller turns
 * into a re-submitted intent with the choice recorded → the re-resolve completes. `onDismiss`
 * hides it locally.
 *
 * **It is the body of THE ACTION ASKS, a card docked at the map's bottom centre, not a
 * screen-blocking overlay** (`website-shell` 4.2). The host (`CombatApp`) carries the card's title;
 * this renders only the inner content — the prompt sentence, the options, a Cancel — in the white
 * brutalist paper idiom the combat-window bodies wear. Non-blocking on purpose:
 * the decider can still pan/read the board while choosing. The `role="dialog"` + `aria-label`
 * stay on the body so assistive tech (and the brace e2e) still find it.
 *
 * **Intentionally generic — keyed only by `promptId`/options.** It knows nothing about
 * attacks, Custom Paint Job, or any specific effect; it renders whatever the authored
 * `choose` atom offered. A new kind of prompt (a reaction, a tech menu, a save→branch
 * decision) reuses this untouched — the transport's *shape* never changes, only what fills it.
 *
 * **`answerKind === "direction"` renders no option buttons.** A spatial decision has no discrete
 * list to pick from — the answer comes from clicking a cell on the board (`CombatApp`'s
 * click-a-cell mode, armed the moment this prompt arrives), not from this window. The window still
 * hosts the pending-prompt affordance + Cancel; it just shows an instruction sentence in place of
 * `pm-opts`. A `"cell"` or `"combatant"` prompt never reaches this window: it is picked on the board and
 * its banner asks the question (`opensPromptWindow`, `cellPick.ts`, `unitPick.ts`).
 *
 * **A Covering Fire offer's path cells fold into one "Pick where along the path" button**
 * (`promptButtonOptions`, `coveringFireSplit.ts`). It answers `PICK_ALONG_PATH_OPTION`, which the
 * host turns into a board pick of the cell rather than a submission.
 *
 * **`deciderName` renders the `.pm-for` line — "For <name>" — and is the ONLY thing here that
 * names WHICH combatant is being asked.** `prompt.prompt` is the authored, generic RAW sentence
 * (Brace's is "Brace? Resistance to all damage…", `reactionRegistry.ts`), so it names nobody. That
 * was harmless while one attack raised at most one prompt; an area attack asks every caught mech in
 * turn on the SAME client (`area-attack-reaction-offers` D1), and two identical-looking modals in a
 * row is a decider answering for a mech they can't identify. The host resolves the name off the
 * live encounter state (`promptDeciderName`, `routes/home/combatApp/predicates.ts`) and omits the
 * prop when `prompt.who` names no combatant this client can see — the line then doesn't render at
 * all rather than printing a raw id.
 *
 * **A damage reaction reads as a question and two tiles, not a rules paragraph**
 * (`comms-attack-reaction-cards` 4.2). A prompt passing `hasReactionStakes` (`incomingDamage`, a
 * `title` and a `summary`: Brace) renders `Brace against Ronin's hit?`, the shared {@link ReactionStakes}
 * tiles and the one-line `summary`, the same body COMMS's `DecisionCard` shows. Every other prompt
 * keeps its authored sentence: choose-a-cell, tech menus, Overwatch, and the damage offers whose
 * reacted figure isn't the target's Resistance (Intercede's "take X's damage instead?", an authored
 * "use Rebound against this damage?"). `sourceName` is the attacker's
 * display name; absent, the question reads `Brace?`.
 */
import type { PromptRequest } from "@lancer-vtt/shared";
import { promptButtonOptions } from "./coveringFireSplit";
import { ReactionStakes, acceptOption, hasReactionStakes, reactionQuestion } from "./ReactionStakes";
import "./PromptModal.css";

export function PromptModal({
  prompt,
  deciderName,
  sourceName,
  onAnswer,
  onDismiss,
}: {
  prompt: PromptRequest;
  deciderName?: string;
  sourceName?: string;
  onAnswer: (optionId: string) => void;
  onDismiss: () => void;
}) {
  return (
    <div className="pm-body" role="dialog" aria-label="resolution prompt">
      {deciderName !== undefined && <p className="pm-for">For {deciderName}</p>}
      {hasReactionStakes(prompt) ? (
        <>
          <p className="pm-question">{reactionQuestion(prompt.title, sourceName)}</p>
          <ReactionStakes
            dmg={prompt.incomingDamage}
            acceptLabel={acceptOption(prompt.options)?.label ?? prompt.title}
            summary={prompt.summary}
          />
        </>
      ) : (
        <p className="pm-prompt">{prompt.prompt}</p>
      )}
      {prompt.answerKind === "direction" ? (
        <p className="pm-direction-hint">Click a cell on the board to choose a direction.</p>

      ) : (
        <ul className="pm-opts">
          {promptButtonOptions(prompt.options).map((opt) => (
            <li key={opt.id}>
              <button onClick={() => onAnswer(opt.id)} className="pm-opt">
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button onClick={onDismiss} className="pm-cancel">
        Cancel
      </button>
    </div>
  );
}
